/* eslint-disable indent */
import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { Field } from 'react-final-form';
import { sortBy } from 'lodash';
import {
  Accordion,
  AccordionSet,
  Button,
  Checkbox,
  Col,
  InfoPopover,
  Label,
  Loading,
  Pane,
  PaneFooter,
  Row,
  Select,
  TextField,
} from '@folio/stripes/components';
import stripesFinalForm from '@folio/stripes/final-form';

const SelfCheckoutDeviceForm = ({
  handleSubmit,
  pristine,
  submitting,
  initialValues,
  parentResources,
  onCancel,
  intl,
}) => {
  const isEditing = !!initialValues?.id;

  const servicePoints = sortBy(
    parentResources?.servicePoints?.records || [],
    'name',
  );

  // Get translated service point name for pane title
  const currentServicePoint = servicePoints.find(
    (sp) => sp.id === initialValues?.servicePointId,
  );
  const translatedServicePointName = currentServicePoint?.name
    ? intl.formatMessage({
        id: `ui-tenant-settings.servicePoints.name.${currentServicePoint.name}`,
        defaultMessage: currentServicePoint.name,
      })
    : null;

  // Get used service point IDs from existing devices
  const usedServicePointIds = (parentResources?.entries?.records || [])
    .filter((entry) => entry.id !== initialValues?.id) // Exclude current device when editing
    .map((entry) => entry.configName?.replace('selfCheckoutConfig.', ''))
    .filter(Boolean);

  // Filter available service points (exclude already used ones)
  const availableServicePoints = servicePoints.filter(
    (sp) => !usedServicePointIds.includes(sp.id),
  );

  const servicePointOptions = availableServicePoints.map((sp) => ({
    value: sp.id,
    label: intl.formatMessage({
      id: `ui-tenant-settings.servicePoints.name.${sp.name}`,
      defaultMessage: sp.name,
    }),
  }));

  const getFooter = () => (
    <PaneFooter
      renderStart={
        <Button buttonStyle="default" onClick={onCancel}>
          <FormattedMessage id="stripes-core.button.cancel" />
        </Button>
      }
      renderEnd={
        <Button
          onClick={handleSubmit}
          disabled={pristine || submitting}
          buttonStyle="primary"
        >
          <FormattedMessage id="stripes-core.button.save" />
        </Button>
      }
    />
  );

  return (
    <Pane
      defaultWidth="fill"
      fluidContentWidth
      paneTitle={
        isEditing && translatedServicePointName ? (
          `${intl.formatMessage({ id: 'ui-circulation.settings.selfCheckout.device.edit' })} - ${translatedServicePointName}`
        ) : isEditing ? (
          <FormattedMessage id="ui-circulation.settings.selfCheckout.device.edit" />
        ) : (
          <FormattedMessage id="ui-circulation.settings.selfCheckout.device.add" />
        )
      }
      footer={getFooter()}
    >
      <form id="device-form">
        <AccordionSet>
          <Accordion
            label={
              <>
                <FormattedMessage id="ui-circulation.settings.selfCheckout.device.generalInfo" />
                <InfoPopover
                  content={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.generalInfo.help" />
                  }
                />
              </>
            }
            id="device-general-info"
          >
            <Row>
              <Col xs={12} md={6}>
                <Field
                  name="servicePointId"
                  component={Select}
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.servicePoint" />
                  }
                  placeholder={intl.formatMessage({
                    id: 'ui-circulation.settings.selfCheckout.device.selectServicePoint',
                  })}
                  dataOptions={servicePointOptions}
                  required

                />
              </Col>
              <Col xs={12} md={6}>
                <Field
                  name="libraryName"
                  component={TextField}
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.libraryName" />
                  }
                  placeholder={intl.formatMessage({
                    id: 'ui-circulation.settings.selfCheckout.device.selectLibrary',
                  })}
                  required
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Field
                  name="terminalLocation"
                  component={TextField}
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.terminalLocation" />
                  }
                  placeholder={intl.formatMessage({
                    id: 'ui-circulation.settings.selfCheckout.device.selectLocation',
                  })}
                />
              </Col>
            </Row>
          </Accordion>

          <Accordion
            label={
              <>
                <FormattedMessage id="ui-circulation.settings.selfCheckout.device.settings" />
                <InfoPopover
                  content={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.settings.help" />
                  }
                />
              </>
            }
            id="device-settings"
          >
            <Row>
              <Col xs={12} md={6}>
                <Field
                  name="timeoutPeriod"
                  component={TextField}
                  type="number"
                  label={
                    <>
                      <FormattedMessage id="ui-circulation.settings.selfCheckout.device.timeoutPeriod" />
                      <InfoPopover
                        content={
                          <FormattedMessage id="ui-circulation.settings.selfCheckout.device.timeoutPeriod.help" />
                        }
                      />
                    </>
                  }
                  min="0"
                  max="999"
                  placeholder="5"
                />
              </Col>
              <Col xs={12} md={6}>
                <Field
                  name="retriesAllowed"
                  component={TextField}
                  type="number"
                  label={
                    <>
                      <FormattedMessage id="ui-circulation.settings.selfCheckout.device.retriesAllowed" />
                      <InfoPopover
                        content={
                          <FormattedMessage id="ui-circulation.settings.selfCheckout.device.retriesAllowed.help" />
                        }
                      />
                    </>
                  }
                  min="0"
                  max="999"
                  placeholder="3"
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <Label>
                  <FormattedMessage id="ui-circulation.settings.selfCheckout.device.allowedOperations" />
                </Label>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4}>
                <Field
                  name="checkinOk"
                  component={Checkbox}
                  type="checkbox"
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.checkinOk" />
                  }
                />
              </Col>
              <Col xs={12} md={4}>
                <Field
                  name="checkoutOk"
                  component={Checkbox}
                  type="checkbox"
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.checkoutOk" />
                  }
                />
              </Col>
              <Col xs={12} md={4}>
                <Field
                  name="acsRenewalPolicy"
                  component={Checkbox}
                  type="checkbox"
                  label={
                    <FormattedMessage id="ui-circulation.settings.selfCheckout.device.acsRenewalPolicy" />
                  }
                />
              </Col>
            </Row>
          </Accordion>
        </AccordionSet>
      </form>
    </Pane>
  );
};

SelfCheckoutDeviceForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  pristine: PropTypes.bool,
  submitting: PropTypes.bool,
  initialValues: PropTypes.object,
  parentResources: PropTypes.object,
  onCancel: PropTypes.func,
  intl: PropTypes.object.isRequired,
};

export default stripesFinalForm({
  navigationCheck: true,
  enableReinitialize: true,
  keepDirtyOnReinitialize: false,
  destroyOnUnregister: false,
  forceUnregisterOnUnmount: false,
})(injectIntl(SelfCheckoutDeviceForm));
